'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { aiModelsApi, ControlNetGenerateResponse } from '@/lib/ai-models-api';

export default function ControlNetPage() {
  const [image, setImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [controlType, setControlType] = useState('canny');
  const [width, setWidth] = useState(1024);
  const [height, setHeight] = useState(1024);
  const [steps, setSteps] = useState(30);
  const [guidanceScale, setGuidanceScale] = useState(7.5);
  const [controlnetScale, setControlnetScale] = useState(0.5);
  const [cannyLow, setCannyLow] = useState(100);
  const [cannyHigh, setCannyHigh] = useState(200);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ControlNetGenerateResponse | null>(null);

  const handleImageChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.readAsDataURL(file);
    });

    setImage(base64);
    setImagePreview(URL.createObjectURL(file));
    setResult(null);
    setError(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!image || !prompt) return;

    setLoading(true);
    setError(null);

    try {
      const response = await aiModelsApi.controlnetGenerate(image, prompt, {
        control_type: controlType,
        negative_prompt: negativePrompt,
        width,
        height,
        num_inference_steps: steps,
        guidance_scale: guidanceScale,
        controlnet_conditioning_scale: controlnetScale,
        canny_low_threshold: cannyLow,
        canny_high_threshold: cannyHigh,
      });

      setResult(response);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[var(--bg-primary)]">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-[var(--border-subtle)]">
        <div className="max-w-7xl mx-auto px-6 h-12 flex items-center gap-4">
          <Link
            href="/ai-models"
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </Link>
          <h1 className="text-lg font-semibold">ControlNet - Guided Generation</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Parameters */}
            <div className="space-y-6">
              <form onSubmit={handleSubmit} className="bg-[var(--bg-elevated)] rounded-xl p-6 space-y-5">
                <h2 className="text-lg font-semibold">Parameters</h2>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">Control Image</label>
                  <div className="relative">
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Input"
                          className="w-full h-40 object-contain bg-[var(--bg-primary)] rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImage(null);
                            setImagePreview(null);
                            setResult(null);
                          }}
                          className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-red-500 rounded transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-[var(--border-subtle)] rounded-lg cursor-pointer hover:border-[var(--accent)] transition-colors">
                        <svg className="w-10 h-10 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                        </svg>
                        <span className="mt-2 text-sm text-[var(--text-muted)]">Upload image</span>
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      </label>
                    )}
                  </div>
                </div>

                {/* Control Type */}
                <div>
                  <label className="block text-sm font-medium mb-2">Control Type</label>
                  <select
                    value={controlType}
                    onChange={(e) => setControlType(e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  >
                    <option value="canny">Canny (Edge Detection)</option>
                    <option value="depth">Depth Map</option>
                  </select>
                </div>

                {/* Prompt */}
                <div>
                  <label className="block text-sm font-medium mb-2">Prompt</label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none h-20 resize-none"
                    placeholder="Describe the image you want to generate..."
                  />
                </div>

                {/* Negative Prompt */}
                <div>
                  <label className="block text-sm font-medium mb-2">Negative Prompt</label>
                  <input
                    type="text"
                    value={negativePrompt}
                    onChange={(e) => setNegativePrompt(e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                    placeholder="What to avoid..."
                  />
                </div>

                {/* Dimensions */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Width</label>
                    <select
                      value={width}
                      onChange={(e) => setWidth(parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                    >
                      <option value={512}>512</option>
                      <option value={768}>768</option>
                      <option value={1024}>1024</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Height</label>
                    <select
                      value={height}
                      onChange={(e) => setHeight(parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                    >
                      <option value={512}>512</option>
                      <option value={768}>768</option>
                      <option value={1024}>1024</option>
                    </select>
                  </div>
                </div>

                {/* Steps */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Steps: {steps}
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="50"
                    value={steps}
                    onChange={(e) => setSteps(parseInt(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                </div>

                {/* Guidance Scale */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Guidance Scale: {guidanceScale.toFixed(1)}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="15"
                    step="0.5"
                    value={guidanceScale}
                    onChange={(e) => setGuidanceScale(parseFloat(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                </div>

                {/* ControlNet Scale */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    ControlNet Strength: {controlnetScale.toFixed(2)}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1.5"
                    step="0.05"
                    value={controlnetScale}
                    onChange={(e) => setControlnetScale(parseFloat(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                </div>

                {/* Canny Thresholds */}
                {controlType === 'canny' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Canny Low: {cannyLow}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="255"
                        value={cannyLow}
                        onChange={(e) => setCannyLow(parseInt(e.target.value))}
                        className="w-full accent-[var(--accent)]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Canny High: {cannyHigh}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="255"
                        value={cannyHigh}
                        onChange={(e) => setCannyHigh(parseInt(e.target.value))}
                        className="w-full accent-[var(--accent)]"
                      />
                    </div>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!image || !prompt || loading}
                  className="w-full py-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Generating...' : 'Generate'}
                </button>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}
              </form>
            </div>

            {/* Center: Control Signal */}
            <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4">Control Signal</h2>

              {result?.control_image ? (
                <div className="space-y-4">
                  <img
                    src={`data:image/png;base64,${result.control_image}`}
                    alt="Control"
                    className="w-full rounded-lg"
                  />
                  <p className="text-sm text-[var(--text-muted)]">
                    {result.control_type === 'canny' ? 'Canny edge detection applied' : 'Depth map generated'}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-32 text-center">
                  <svg className="w-16 h-16 text-[var(--text-muted)] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
                  </svg>
                  <p className="text-[var(--text-muted)]">Control signal will appear here</p>
                </div>
              )}
            </div>

            {/* Right: Generated Image */}
            <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4">Generated Image</h2>

              {!result && !loading && (
                <div className="flex flex-col items-center justify-center py-32 text-center">
                  <svg className="w-16 h-16 text-[var(--text-muted)] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                  <p className="text-[var(--text-muted)]">Generated image will appear here</p>
                </div>
              )}

              {loading && (
                <div className="flex flex-col items-center justify-center py-32">
                  <div className="w-12 h-12 border-3 border-[var(--accent)] border-t-transparent rounded-full animate-spin mb-4" />
                  <p className="text-[var(--text-muted)]">Generating image...</p>
                  <p className="text-xs text-[var(--text-muted)] mt-1">This may take a while</p>
                </div>
              )}

              {result && !loading && (
                <div className="space-y-4">
                  <img
                    src={`data:image/png;base64,${result.image}`}
                    alt="Generated"
                    className="w-full rounded-lg"
                  />

                  {/* Download Button */}
                  <a
                    href={`data:image/png;base64,${result.image}`}
                    download="controlnet_output.png"
                    className="block w-full py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors text-center"
                  >
                    Download Image
                  </a>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 p-4 bg-[var(--bg-primary)] rounded-lg">
                    <div>
                      <p className="text-xs text-[var(--text-muted)] uppercase">Size</p>
                      <p className="text-sm font-semibold">{result.width}x{result.height}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--text-muted)] uppercase">Steps</p>
                      <p className="text-sm font-semibold">{result.num_inference_steps}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--text-muted)] uppercase">Time</p>
                      <p className="text-sm font-semibold">{(result.inference_time_ms / 1000).toFixed(1)}s</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
